import React from "react";
import { Box, Typography, IconButton, Tooltip, Button } from "@mui/material";
import { useGameState } from "../providers/GameStateProvider";
import { useSupabase, supabase } from "../providers/SupabaseProvider";
import { MdOutlineAttachMoney, MdOutlineMoneyOff } from "react-icons/md";
import { useNavigate } from "react-router-dom";
// FaFish handled inside TokenDisplay
import TokenDisplay from "./TokenDisplay";
import { backendService } from "../services/backendService";
import { TOOLBAR_HEIGHT } from "../game/BackgroundImage";

const LevelInfoBar: React.FC = () => {
  const {
    currentLevel,
    statsLevel,
    adultMode,
    setAdultMode,
    setCurrentLevel,
    completedLevels,
  } = useGameState();
  const { user } = useSupabase();
  const navigate = useNavigate();

  const handleAdultModeToggle = async () => {
    const newMode = !adultMode;
    setAdultMode(newMode);

    // Persist preference if the user is logged in
    if (user) {
      const { error } = await supabase.auth.updateUser({
        data: { adultMode: newMode },
      });
      if (error) {
        console.error("Failed to update adult mode preference:", error);
      }
    }
  };

  const handleLoadYICLevel = async (levelName: string) => {
    if (!user) return;

    try {
      const level = await backendService.fetchLevelByName(user.id, levelName);
      setCurrentLevel(level);

      // Force restart the Phaser scene with new level data
      setTimeout(() => {
        const game = (window as any).PhaserGame;
        if (game) {
          // Stop the current scene if running
          const runningScene = (window as any).PhaserSceneReact;
          if (runningScene && runningScene.scene.key === "BudgetSceneReact") {
            console.log(
              "Force restarting BudgetSceneReact for YIC level:",
              levelName,
            );
            game.scene.stop("BudgetSceneReact");
          }

          // Start fresh scene with new level data
          game.scene.start("BudgetSceneReact", {
            categories: level.categories,
          });
        }
      }, 100);
    } catch (error) {
      console.error(`Failed to load ${levelName} level:`, error);
    }
  };

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: `${TOOLBAR_HEIGHT}px`,
        px: 2,
        py: 1,
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        background: "rgba(0,0,0,0.6)",
        backdropFilter: "blur(4px)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        {/* Level info - Left */}
        <Box>
          <Typography variant="h5" color="white">
            {statsLevel != null
              ? `Level ${statsLevel}`
              : currentLevel
                ? `Level ${currentLevel.input_level}`
                : "Level"}
          </Typography>
          <Typography variant="subtitle2" color="#CCCCCC">
            #{currentLevel?.nickname || ""}
          </Typography>
        </Box>

        {/* YIC Scenario Buttons - Center */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: "white", mr: 1, fontSize: "12px" }}
          >
            YIC scenarios:
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleLoadYICLevel("yic-kane")}
            sx={{
              color: "white",
              borderColor: completedLevels.includes("yic-kane")
                ? "#4ade80"
                : "white",
              fontSize: "11px",
              padding: "2px 8px",
              minWidth: "auto",
              position: "relative",
              "&:hover": {
                borderColor: completedLevels.includes("yic-kane")
                  ? "#22c55e"
                  : "#bdc3c7",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            Kane
            {completedLevels.includes("yic-kane") && (
              <Box
                component="span"
                sx={{
                  position: "absolute",
                  top: -4,
                  right: -4,
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  backgroundColor: "#4ade80",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  color: "white",
                  fontWeight: "bold",
                }}
              >
                ✓
              </Box>
            )}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleLoadYICLevel("yic-liz")}
            sx={{
              color: "white",
              borderColor: completedLevels.includes("yic-liz")
                ? "#4ade80"
                : "white",
              fontSize: "11px",
              padding: "2px 8px",
              minWidth: "auto",
              position: "relative",
              "&:hover": {
                borderColor: completedLevels.includes("yic-liz")
                  ? "#22c55e"
                  : "#bdc3c7",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            Liz
            {completedLevels.includes("yic-liz") && (
              <Box
                component="span"
                sx={{
                  position: "absolute",
                  top: -4,
                  right: -4,
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  backgroundColor: "#4ade80",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  color: "white",
                  fontWeight: "bold",
                }}
              >
                ✓
              </Box>
            )}
          </Button>
        </Box>

        {/* Tokens and mode toggle - Right */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          {/* Tokens */}
          <TokenDisplay value={user?.tokens ?? 0} />

          {/* Adult / Kid toggle icon */}
          <Tooltip title={adultMode ? "Show Kibbles" : "Show Dollars"} arrow style={{display: "none" /* TODO: re-enable */}}>
            <IconButton
              onClick={handleAdultModeToggle}
              sx={{
                color: adultMode ? "#34d399" : "rgba(255,255,255,0.5)",
                "&:hover": {
                  backgroundColor: adultMode
                    ? "rgba(52,211,153,0.2)"
                    : "rgba(255,255,255,0.1)",
                },
              }}
            >
              {adultMode ? (
                <MdOutlineAttachMoney size={24} />
              ) : (
                <MdOutlineMoneyOff size={24} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default LevelInfoBar;
